import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  ApiClient, KuecheBeobachtung, KuecheKandidat, KuecheOffeneZuordnungen,
  KuecheZuordnungslauf,
} from '../api';
import { IconOk, IconWarnung } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import { ErrorState, LoadingState } from '../muster';

const KOPFTEXT = 'Beobachtungen der Planauswertung auf reale Modellbauteile abbilden: '
  + 'PDF übernehmen, Achsen registrieren, Zuordnung rechnen — und prüfen, was nicht '
  + 'eindeutig war.';

/**
 * Fremdmodelle > Küche > Zuordnung.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE FRAGE DIESER SEITE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Plan sagt: „Hier, an dieser Stelle, 400 V und 12,4 kW." Das Modell sagt:
 * „Hier steht ein Kombidämpfer." Beide meinen dasselbe Gerät, und niemand hat
 * es aufgeschrieben.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM ALLE KANDIDATEN SICHTBAR SIND, NICHT NUR DER BESTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 12 cm zum Ersten und 13 cm zum Zweiten ist keine Entscheidung, sondern ein
 * Zufall, der wie eine aussieht. Wer hier entscheidet, muss den Zweitbesten
 * sehen — sonst bestätigt er eine Reihenfolge, die er nicht kennt.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DREI WEGE, UND ALLE DREI SIND EINE AUSSAGE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   zuordnen       — diese Beobachtung gehört zu diesem Bauteil
 *   keine Küchendaten — sie gehört gar nicht hierher
 *   ohne Ziel      — Küchendaten, aber kein Bauteil im Modell
 *
 * Einen vierten Weg „später" gibt es absichtlich nicht: Das wäre der Zustand
 * OFFEN, und den hat die Beobachtung bereits.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

/** Die Beobachtungsarten in lesbarer Form. */
const ART: Record<string, string> = {
  DEVICE_LABEL: 'Gerätebeschriftung',
  TECHNICAL_CALLOUT: 'Technischer Callout',
  ELECTRICAL_POINT: 'Elektro',
  COLD_WATER_POINT: 'Kaltwasser',
  HOT_WATER_POINT: 'Warmwasser',
  WASTE_WATER_POINT: 'Abwasser',
  GAS_POINT: 'Gas',
  DATA_POINT: 'Daten',
  SUPPLY_AIR_POINT: 'Zuluft',
  EXTRACT_AIR_POINT: 'Abluft',
  LIGHTING_POINT: 'Beleuchtung',
  OTHER_HKLSE_POINT: 'HKLSE, sonstige',
};

export function Kuechenmapping() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [stand, setStand] = useState<KuecheOffeneZuordnungen | null>(null);
  const [lauf, setLauf] = useState<KuecheZuordnungslauf | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = useCallback(async () => {
    if (!projekt) return;
    try {
      setStand(await api.kuecheZuordnungOffen(projekt.id, 100));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projekt]);

  useEffect(() => { void laden(); }, [laden]);

  const schritt = async (
    name: string, tun: () => Promise<unknown>, text: (e: never) => string) => {
    if (!projekt) return;
    setLaeuft(name);
    setMeldung(null);
    try {
      const e = await tun();
      setMeldung(text(e as never));
      await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const entscheiden = async (
    b: KuecheBeobachtung, tun: () => Promise<unknown>) => {
    try {
      await tun();
      // Nur die eine Zeile entfernen statt alles neu zu laden: Bei hundert
      // Entscheidungen hintereinander wäre jedes Mal ein Ladezustand
      // dazwischen — und die Liste spränge unter dem Zeiger weg.
      setStand((s) => s === null ? s : {
        ...s,
        gesamt: s.gesamt - 1,
        gezeigt: s.gezeigt - 1,
        beobachtungen: s.beobachtungen.filter((x) => x.id !== b.id),
      });
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    }
  };

  if (fehler) {
    return (
      <>
        <PageHeader titel="Zuordnung Plan → Modell" beschreibung={KOPFTEXT} />
        <ErrorState
          meldung="Der Stand der Zuordnung konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => void laden()}
        />
      </>
    );
  }
  if (!stand) {
    return (
      <>
        <PageHeader titel="Zuordnung Plan → Modell" beschreibung={KOPFTEXT} />
        <LoadingState text="Stand der Zuordnung wird geladen …" zeilen={6} spalten={3} />
      </>
    );
  }

  return (
    <div className="seite">
      <PageHeader
        titel="Zuordnung Plan → Modell"
        beschreibung={KOPFTEXT}
        aktionen={(
        <>
          <button
            type="button"
            className="m-knopf"
            disabled={laeuft !== null}
            title={'Die 1.066 Anschlusspunkte und 514 Beschriftungen der '
                 + 'Planauswertung als Beobachtungen übernehmen. Wiederholbar '
                 + '— was schon da ist, kommt nicht doppelt.'}
            onClick={() => void schritt('pdf',
              () => api.kuechePdfUebernehmen(projekt!.id),
              (e: { neu: number; bekannt: number }) =>
                `${e.neu} Beobachtung(en) übernommen, ${e.bekannt} waren `
                + 'bereits vorhanden.')}
          >
            {laeuft === 'pdf' ? 'übernimmt …' : 'PDF-Aufnahme übernehmen'}
          </button>

          <button
            type="button"
            className="m-knopf"
            disabled={laeuft !== null}
            title={'Aus je zwei Achsschnittpunkten die Abbildung Planblatt → '
                 + 'Host bestimmen. Sie liefert den Suchpunkt — nicht die '
                 + 'Markerposition.'}
            onClick={() => void schritt('achsen',
              () => api.kuecheAchsenRegistrieren(projekt!.id),
              (e: { registriert: number; plaene: number; suchpunkte: number }) =>
                `${e.registriert} von ${e.plaene} Plänen registriert, `
                + `${e.suchpunkte} Suchpunkte gesetzt.`)}
          >
            {laeuft === 'achsen' ? 'registriert …' : 'Achsen registrieren'}
          </button>

          <button
            type="button"
            className="m-knopf m-knopf-haupt"
            disabled={laeuft !== null}
            onClick={() => void schritt('zuordnung',
              () => api.kuecheZuordnungRechnen(projekt!.id),
              (e: KuecheZuordnungslauf) => {
                setLauf(e);
                return `${e.automatisch} automatisch zugeordnet, `
                     + `${e.zurPruefung} zur Prüfung, ${e.ohneKandidat} ohne `
                     + 'Kandidat.';
              })}
          >
            {laeuft === 'zuordnung' ? 'rechnet …' : 'Zuordnung neu rechnen'}
          </button>
        </>
        )}
      />

      {meldung && (
        <div className="hinweis" style={{ marginBottom: 14 }}>{meldung}</div>
      )}

      {lauf && lauf.hinweise.length > 0 && (
        <div className="hinweis warnung" style={{ marginBottom: 14 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {lauf.hinweise.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </div>
      )}

      <div className="kennzahlen" style={{ marginBottom: 18 }}>
        <div className="kennzahl">
          <div className="kennzahl-wert"
               style={stand.gesamt > 0 ? { color: 'var(--warnung)' } : undefined}>
            {stand.gesamt}
          </div>
          <div className="kennzahl-name">offene Beobachtungen</div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{stand.gezeigt}</div>
          <div className="kennzahl-name">hier gezeigt</div>
        </div>
      </div>

      {stand.gesamt === 0 && (
        <div className="hinweis">
          <IconOk size={14} /> Keine offene Zuordnung. Jede technische
          Beobachtung trägt genau einen Zustand — zugeordnet, ausgeschlossen
          oder ausdrücklich ohne Ziel.
        </div>
      )}

      {stand.beobachtungen.map((b) => (
        <Zeile key={b.id} b={b}
               annehmen={(k) => void entscheiden(b, () =>
                 api.kuecheZuordnungAnnehmen(projekt!.id, b.id, k.komponenteId))}
               ausschliessen={() => void entscheiden(b, () =>
                 api.kuecheZuordnungAusschliessen(projekt!.id, b.id))}
               ohneZiel={() => void entscheiden(b, () =>
                 api.kuecheZuordnungOhneZiel(projekt!.id, b.id))} />
      ))}
    </div>
  );
}

function Zeile({ b, annehmen, ausschliessen, ohneZiel }: {
  b: KuecheBeobachtung;
  annehmen: (k: KuecheKandidat) => void;
  ausschliessen: () => void;
  ohneZiel: () => void;
}) {
  const beste = b.kandidaten[0];
  const zweite = b.kandidaten[1];

  // Der Abstand zwischen den beiden besten ist die eigentliche Auskunft:
  // Ist er klein, hat die Automatik zu Recht nicht entschieden.
  const knapp = beste !== undefined && zweite !== undefined
             && beste.bewertung - zweite.bewertung < 15;

  return (
    <div className="karte" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px' }}>
          <div>
            <span className="marke neutral">
              {ART[b.beobachtungsart] ?? b.beobachtungsart}
            </span>
            {b.positionText && (
              <strong style={{ marginLeft: 8 }}>Pos. {b.positionText}</strong>
            )}
            {b.hostKoordinateStatus !== 'BERECHNET' && (
              <span className="marke warnung" style={{ marginLeft: 8 }}
                    title={'Ohne Suchpunkt läuft die Zuordnung allein über '
                         + 'Text — das ist zulässig, aber schwächer.'}>
                ohne Suchpunkt
              </span>
            )}
          </div>

          {b.rohtext && (
            <div className="sehr-schwach" style={{ marginTop: 4 }}>
              {b.rohtext}
            </div>
          )}

          <div className="sehr-schwach" style={{ marginTop: 4 }}>
            {b.raumText && <>Raum laut Plan: {b.raumText} · </>}
            {b.hostXKandidatM !== null && b.hostYKandidatM !== null && (
              <>Suchpunkt {b.hostXKandidatM.toFixed(2)} / {b.hostYKandidatM.toFixed(2)}</>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <button className="zweitrangig" onClick={ausschliessen}
                  title="Das sind keine Küchendaten.">
            keine Küchendaten
          </button>
          <button className="zweitrangig" onClick={ohneZiel}
                  title={'Küchendaten, aber kein Bauteil im Modell. Eine '
                       + 'Aussage — kein offener Punkt.'}>
            ohne Ziel
          </button>
        </div>
      </div>

      {knapp && (
        <div className="hinweis warnung" style={{ marginTop: 10 }}>
          <IconWarnung size={14} /> Die beiden besten Kandidaten liegen nur
          {' '}{(beste.bewertung - zweite.bewertung).toFixed(0)} Punkte
          auseinander. Deshalb hat die Automatik nicht entschieden.
        </div>
      )}

      {b.kandidaten.length === 0 ? (
        <div className="hinweis" style={{ marginTop: 10 }}>
          Kein Kandidat im Suchradius. Entweder fehlt die Achsregistrierung
          dieses Plans, oder das Gerät steht nicht im Modell — dann ist
          „ohne Ziel" die richtige Aussage.
        </div>
      ) : (
        <table style={{ width: '100%', marginTop: 10 }}>
          <thead>
            <tr>
              <th style={{ width: 60 }}>Punkte</th>
              <th>Bauteil</th>
              <th>Ebene</th>
              <th style={{ textAlign: 'right' }}>Abstand</th>
              <th>Merkmale</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {b.kandidaten.map((k) => (
              <tr key={k.komponenteId}>
                <td><strong>{k.bewertung.toFixed(0)}</strong></td>
                <td>
                  {k.kucaTyp ?? k.geraet ?? '—'}
                  {k.positionIdModell && (
                    <span className="sehr-schwach"> · Pos. {k.positionIdModell}</span>
                  )}
                  {k.bauteilklasse !== 'GERAET' && (
                    <span className="marke neutral" style={{ marginLeft: 6 }}>
                      {k.bauteilklasse === 'LUEFTUNGSDECKE'
                        ? 'Lüftungsdecke' : 'Leuchte'}
                    </span>
                  )}
                </td>
                <td>{k.etage}</td>
                <td style={{ textAlign: 'right' }}>
                  {k.abstandBboxM !== null
                    ? `${k.abstandBboxM.toFixed(2)} m`
                    : k.abstandXyM !== null
                      ? `${k.abstandXyM.toFixed(2)} m` : '—'}
                </td>
                <td className="sehr-schwach">
                  {k.positionTrifft === true && 'Position · '}
                  {k.innerhalbBbox === true && 'innerhalb · '}
                  {k.gleicherRaum === true && 'Raum · '}
                  {k.gleicheEbene === false && 'andere Ebene'}
                </td>
                <td>
                  <button onClick={() => annehmen(k)}>zuordnen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
